/**
 * Shared, PURE helpers for building Fleet Timeseries chart configuration from saved `fleet_timeseries`
 * chart objects + per-source rows. Extracted from `reports/gp50/day/explore/FleetTimeSeries.tsx` so the live
 * timeseries page (`reports/gp50/live/explore/FleetTimeseries.tsx`) can reuse the exact same object-loading,
 * channel-extraction and series-expansion logic while sourcing its rows from the live stream instead of the
 * historical unifiedDataStore fetch.
 *
 * These functions are intentionally free of Solid reactivity and side effects (only console debug/warn) so
 * they are trivially testable and safe to call from either page.
 */

import { apiEndpoints } from '@config/env';
import { getData } from './global';
import { debug, warn } from './console';

export type FleetSource = { source_id: number; source_name: string; dataset_id: number };
/** Per-source raw rows keyed by source_id (each row is a channel bag incl. `Datetime`). */
export type FleetSourceRows = Map<number, any[]>;
/** Series-key ("<chartIndex>_<seriesIndex>") → the sources that series needs data for. */
export type SeriesSourceMap = Map<string, FleetSource[]>;

export interface FetchFleetTimeseriesObjectsArgs {
  className: string;
  projectId: string | number;
  datasetId: string | number;
  userId: string | number;
  /** Preferred saved-object name (selected page / prop). When absent, the first available object is used. */
  preferredName?: string | null;
}

export interface FetchFleetTimeseriesObjectsResult {
  /** The saved chart objects (`chart_info`), or [] if none. */
  chartObjects: any[];
  /** The object name that was actually resolved/used, or null when none exist. */
  resolvedName: string | null;
}

/**
 * Resolve the `fleet_timeseries` object name (preferred → first available) and fetch its chart objects.
 * Mirrors the network flow in FleetTimeSeries.tsx (object/names → object). Pure w.r.t. app state — the caller
 * owns any signal updates (e.g. setSelectedPage(resolvedName)).
 */
export async function fetchFleetTimeseriesChartObjects(
  args: FetchFleetTimeseriesObjectsArgs
): Promise<FetchFleetTimeseriesObjectsResult> {
  const { className, projectId, datasetId, userId } = args;
  let nameToUse: string | null = (args.preferredName || '').trim() || null;
  if (nameToUse === 'default' || nameToUse === '') nameToUse = null;

  if (!nameToUse) {
    const namesUrl = `${apiEndpoints.app.users}/object/names?class_name=${encodeURIComponent(String(className))}&project_id=${encodeURIComponent(String(projectId))}&user_id=${encodeURIComponent(String(userId))}&parent_name=fleet_timeseries`;
    const namesResponse = await getData(namesUrl);
    const namesList =
      namesResponse?.success && Array.isArray(namesResponse?.data)
        ? (namesResponse.data as { object_name: string }[])
        : [];
    if (namesList.length > 0) {
      const preferred = (args.preferredName || '').trim();
      const inList = preferred
        ? namesList.find((n) => (n.object_name || '').toLowerCase() === preferred.toLowerCase())
        : null;
      nameToUse = inList ? inList.object_name : namesList[0].object_name;
    }
  }

  if (!nameToUse) {
    return { chartObjects: [], resolvedName: null };
  }

  const apiUrl = `${apiEndpoints.app.users}/object?class_name=${encodeURIComponent(String(className))}&project_id=${encodeURIComponent(String(projectId))}&dataset_id=${encodeURIComponent(String(datasetId))}&user_id=${encodeURIComponent(String(userId))}&parent_name=fleet_timeseries&object_name=${encodeURIComponent(nameToUse)}`;
  const response = await getData(apiUrl);
  const chartObjects = response?.data?.chart_info || [];
  return { chartObjects, resolvedName: nameToUse };
}

/**
 * Extract the required channel names from all chart objects' series (x/y/color axes), with `Datetime`
 * guaranteed first. Matches FleetTimeSeries.tsx:979-1008.
 */
export function extractRequiredChannels(chartObjects: any[]): string[] {
  const requiredChannels: string[] = [];
  (chartObjects || []).forEach((chart: any) => {
    if (chart.series && chart.series.length > 0) {
      chart.series.forEach((series: any) => {
        if (series.xaxis && series.xaxis.name) requiredChannels.push(series.xaxis.name);
        if (series.yaxis && series.yaxis.name) requiredChannels.push(series.yaxis.name);
        if (series.colorChannel && series.colorChannel.name) requiredChannels.push(series.colorChannel.name);
      });
    }
  });
  if (!requiredChannels.includes('Datetime')) {
    requiredChannels.unshift('Datetime');
  }
  return requiredChannels;
}

/**
 * Map each series ("<chartIndex>_<seriesIndex>") to the sources it needs, based on `colorBySource`
 * (ALL → every selected source; a name → that source; else → first source). Matches FleetTimeSeries.tsx:1183-1223.
 */
export function buildSeriesSourceMap(chartObjects: any[], sourcesToUse: FleetSource[]): SeriesSourceMap {
  const seriesSourceMap: SeriesSourceMap = new Map();
  (chartObjects || []).forEach((chart: any, chartIndex: number) => {
    if (chart.series && chart.series.length > 0) {
      chart.series.forEach((series: any, seriesIndex: number) => {
        const key = `${chartIndex}_${seriesIndex}`;
        const colorBySource = series.colorBySource;
        if (colorBySource === 'ALL' || colorBySource === 'all') {
          seriesSourceMap.set(key, sourcesToUse);
        } else if (colorBySource) {
          const specificSource = sourcesToUse.find(
            (s) => s.source_name.toLowerCase() === String(colorBySource).toLowerCase()
          );
          if (specificSource) {
            seriesSourceMap.set(key, [specificSource]);
          } else {
            warn(`fleetTimeseriesConfig: Source "${colorBySource}" not found, skipping series`);
            seriesSourceMap.set(key, []);
          }
        } else if (sourcesToUse.length > 0) {
          seriesSourceMap.set(key, [sourcesToUse[0]]);
        } else {
          seriesSourceMap.set(key, []);
        }
      });
    }
  });
  return seriesSourceMap;
}

export interface ExpandChartObjectsArgs {
  chartObjects: any[];
  seriesSourceMap: SeriesSourceMap;
  sourceDataMap: FleetSourceRows;
  /** source_name (lowercased) → color, used for ALL-source series. */
  sourceColorMap: Map<string, string>;
  /** Channels to copy through onto each processed point. */
  validChannels: string[];
}

/**
 * Expand saved chart objects into `PlaybackTimeSeries`/`TimeSeries`-shaped chart configs: for each series,
 * emit one line per mapped source (ALL) or a single line, mapping rows → `{x:Date, y, Datetime, source_id,
 * source_name, [xName], [yName], Race_number?, Leg_number?, Grade?}` sorted ascending by x.
 *
 * Verbatim port of FleetTimeSeries.tsx:1386-1589 (pure — no reactivity). Both the historical fleet page and the
 * live timeseries page call this with their own `sourceDataMap` row provider.
 */
export function expandChartObjectsToConfig(args: ExpandChartObjectsArgs): any[] {
  const { chartObjects, seriesSourceMap, sourceDataMap, sourceColorMap, validChannels } = args;

  return chartObjects.map((chart: any, chartIndex: number) => {
    const expandedSeries: any[] = [];
    const seenSeriesKey = new Set<string>();

    (chart.series || []).forEach((series: any, seriesIndex: number) => {
      const key = `${chartIndex}_${seriesIndex}`;
      const sourcesForSeries = seriesSourceMap.get(key) || [];
      const xName = series.xaxis?.name;
      const yName = series.yaxis?.name;
      const colorBySource = series.colorBySource;

      if (!xName || !yName) {
        warn(`fleetTimeseriesConfig: Series ${seriesIndex} missing xaxis or yaxis, skipping`);
        return;
      }

      const processSourceRows = (source: FleetSource) => {
        const sourceData = sourceDataMap.get(source.source_id) || [];
        const processedData = sourceData.map((item: any) => {
          const dataPoint: any = {
            Datetime: item.Datetime,
            source_id: item.source_id,
            source_name: item.source_name,
          };
          validChannels.forEach((channelName: string) => {
            if (item[channelName] !== undefined) dataPoint[channelName] = item[channelName];
          });
          if (item.Race_number !== undefined) dataPoint.Race_number = item.Race_number;
          if (item.Leg_number !== undefined) dataPoint.Leg_number = item.Leg_number;
          if (item.Grade !== undefined) dataPoint.Grade = item.Grade;
          if (xName === 'Datetime' && item.Datetime) {
            dataPoint.x = new Date(item.Datetime);
          } else if (item[xName] !== undefined) {
            const timestamp = item[xName];
            if (typeof timestamp === 'number') {
              dataPoint.x = timestamp < 946684800 ? new Date(timestamp * 1000) : new Date(timestamp);
            } else {
              dataPoint.x = new Date(timestamp);
            }
          }
          return dataPoint;
        });

        return processedData
          .filter((item: any) => item.x !== undefined && item[yName] !== undefined)
          .map((item: any) => {
            const seriesPoint: any = {
              x: item.x,
              y: item[yName],
              Datetime: item.Datetime,
              source_id: item.source_id,
              source_name: item.source_name,
              [xName]: item[xName],
              [yName]: item[yName],
            };
            if (item.Race_number !== undefined) seriesPoint.Race_number = item.Race_number;
            if (item.Leg_number !== undefined) seriesPoint.Leg_number = item.Leg_number;
            if (item.Grade !== undefined) seriesPoint.Grade = item.Grade;
            return seriesPoint;
          })
          .sort((a: any, b: any) => {
            if (!a.x || !b.x) return 0;
            return a.x.getTime() - b.x.getTime();
          });
      };

      if (colorBySource === 'ALL' || colorBySource === 'all') {
        sourcesForSeries.forEach((source: FleetSource) => {
          const seriesKey = `${yName}|${source.source_id}`;
          if (seenSeriesKey.has(seriesKey)) return;
          seenSeriesKey.add(seriesKey);
          const seriesData = processSourceRows(source);
          const sourceColor =
            sourceColorMap.get(source.source_name.toLowerCase()) || series.color || '#1f77b4';
          expandedSeries.push({
            ...series,
            yaxis: { ...series.yaxis, name: `${series.yaxis.name} - ${source.source_name}` },
            color: sourceColor,
            data: seriesData,
          });
        });
      } else {
        const source = sourcesForSeries[0];
        if (!source) {
          warn(`fleetTimeseriesConfig: No source found for series ${seriesIndex}, skipping`);
          return;
        }
        const seriesData = processSourceRows(source);
        const seriesColor = series.color || '#1f77b4';
        expandedSeries.push({
          ...series,
          color: seriesColor,
          data: seriesData,
        });
      }
    });

    return {
      ...chart,
      chart: `chart_${chartIndex + 1}`,
      series: expandedSeries,
    };
  });
}

/** Small debug pass-through so callers can log with the shared prefix without importing console directly. */
export function debugFleetTimeseriesConfig(...msg: unknown[]): void {
  debug('fleetTimeseriesConfig:', ...msg);
}
